import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Formatters, I18n } from "./engine";

export interface TranslationValue<L extends string, K extends string> {
  locale: L;
  t(key: K, values?: Record<string, string | number>): string;
  /**
   * Like `t`, but the substituted values may be React nodes.
   *
   * Several sentences wrap one value in markup — an address in `<strong>`, a
   * destination in a link. Splitting those into "before" and "after" fragments
   * is the usual workaround and it is a bad one: it hands a translator two
   * half-sentences and hard-codes English word order, which is exactly the kind
   * of copy that comes back correct in isolation and wrong on the page.
   *
   * So the catalogue keeps ONE whole sentence with a `{placeholder}`, and the
   * node is threaded in at render.
   */
  tx(key: K, values: Record<string, ReactNode>): ReactNode;
  fmt: Formatters;
  chooseLocale(locale: L): void;
}

/**
 * Bind an engine to React, typed to one app's own message keys.
 *
 * A factory rather than a single shared context because the two apps have
 * DIFFERENT key unions: a shared context would have to be typed to the union of
 * both, and `t()` on Book would then accept App's keys and vice versa. Each app
 * calls this once and re-exports the pair, so a typo in a key is still a
 * compile error on the surface that made it.
 */
export function createTranslation<L extends string, K extends string>(i18n: I18n<L, K>) {
  const TranslationContext = createContext<TranslationValue<L, K> | null>(null);

  function TranslationProvider({
    children,
    initialLocale,
  }: {
    children: ReactNode;
    /** Injected in tests so a suite does not depend on the runner's navigator. */
    initialLocale?: L;
  }) {
    const [locale, setLocaleState] = useState<L>(() => initialLocale ?? i18n.detectLocale());

    // useMemo rather than relying on the React Compiler: `fmt` builds four Intl
    // formatters, and those are genuinely expensive to construct. This is the
    // documented exception to the no-manual-memo rule, not a habit.
    const value = useMemo<TranslationValue<L, K>>(
      () => ({
        locale,
        t: (key, values) => i18n.translate(locale, key, values),
        tx: (key, values) => {
          // Deliberately translated WITHOUT values so the placeholders survive
          // to be split on here.
          const template = i18n.translate(locale, key);
          return template.split(/(\{\w+\})/g).map((part, index) => {
            const name = /^\{(\w+)\}$/.exec(part)?.[1];
            // An unknown placeholder is left visible rather than blanked —
            // same rule as `translate`: a missing value should be caught in
            // review, not render as a hole in a sentence.
            if (name === undefined || !(name in values)) return part;
            return <Fragment key={`${name}-${index}`}>{values[name]}</Fragment>;
          });
        },
        fmt: i18n.formatters(locale),
        chooseLocale: (next: L) => {
          i18n.setLocale(next);
          setLocaleState(next);
        },
      }),
      [locale]
    );

    // Applied here rather than exported as a hook nobody remembers to call. The
    // first version of this WAS a separate `useDocumentLocale` export, and it
    // was never wired up — same shape as the env guard that shipped in no
    // bundle.
    //
    // Not cosmetic: screen readers pick pronunciation from `lang`, and a
    // Spanish page announced by an English voice is close to unusable. It also
    // drives hyphenation and quote rendering.
    useEffect(() => {
      if (document.documentElement.lang !== locale) {
        document.documentElement.lang = locale;
      }
    }, [locale]);

    return (
      <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>
    );
  }

  function useTranslation(): TranslationValue<L, K> {
    const context = useContext(TranslationContext);
    if (!context) {
      throw new Error("useTranslation must be used inside <TranslationProvider>");
    }
    return context;
  }

  return { TranslationProvider, useTranslation };
}
